import * as React from "react";
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
} from "@mui/material";
import { createTheme, alpha } from "@mui/material/styles";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";

import FileCard from "./FileCard";
import { PreviewKind } from "./FileCard/FileCard";

const CARD_W = 260;

type Queued = { id: string; file: File; preview?: string };

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#A66B7D", light: "#D7A7B5", dark: "#7D4A58", contrastText: "#fff" },
    secondary: { main: "#7AA69F", light: "#B9D3C9", dark: "#5B8578", contrastText: "#fff" },
    background: { default: "#FFFCF7", paper: "#FFFFFF" },
    divider: "#E8DFD6",
    text: { primary: "#2E2A27", secondary: "#6D6159" },
    success: { main: "#6DA67A" },
    warning: { main: "#C9A227" },
    error: { main: "#C05A5A" },
    info: { main: "#7F9ACD" },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,"Noto Sans",sans-serif',
    h1: {
      fontFamily: '"Italianno","Times New Roman",Times,serif',
      fontWeight: 600,
      letterSpacing: 0.5,
      lineHeight: 1.1,
      fontSize: "4rem",
      fontStyle: "italic",
      textAlign: "center",
      color: "#7D4A58",
      textShadow: "0 1px 0 rgba(255,255,255,.6)",
      "@media (max-width:600px)": { fontSize: "3rem" },
      "@media (max-width:425px)": { fontSize: "2.5rem" },
    },
    h2: {
      fontFamily: '"Italianno","Times New Roman",Times,serif',
      fontWeight: 400,
      letterSpacing: 0.25,
      lineHeight: 1.15,
      fontSize: "2rem",
      textAlign: "center",
      color: "#7D4A58",
      textShadow: "0 1px 0 rgba(255,255,255,.6)",
      "@media (max-width:600px)": { fontSize: "2.5rem" },
      "@media (max-width:425px)": { fontSize: "2rem" },
    },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          backgroundColor: "#fffcf7",
        },
        body: {
          backgroundImage: `
            radial-gradient(1200px 600px at -10% -10%, rgba(166,107,125,0.12), transparent 60%),
            radial-gradient(1000px 500px at 120% 0%, rgba(122,166,159,0.12), transparent 55%),
            url("/wedding-texture-white.png")
          `,
          backgroundRepeat: "repeat, no-repeat, repeat",
          backgroundSize: "auto, auto, 512px 512px",
          minHeight: "100vh",
          paddingBottom: "env(safe-area-inset-bottom)",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 12,
          paddingInline: 16,
          transition: "transform .06s ease, box-shadow .2s ease",
          "&:active": { transform: "translateY(1px)" },
          "&.Mui-disabled": {
            opacity: .55,
            border: "1px dashed rgba(0,0,0,.12)",         // ⬅️ disabled nadal widoczny
          },
        },
        containedPrimary: {
          boxShadow: "0 6px 18px rgba(166,107,125,0.25), 0 2px 6px rgba(166,107,125,0.25)",
          "&:hover": {
            boxShadow: "0 8px 22px rgba(166,107,125,0.32), 0 3px 10px rgba(166,107,125,0.28)",
          },
        },
        outlined: {
          borderColor: "rgba(166,107,125,0.35)",
          "&:hover": { borderColor: "rgba(166,107,125,0.6)" },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "::placeholder": {
            color: "rgba(46,42,39,.7)",                   // ⬅️ mocniejszy placeholder
            opacity: 1,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999 },
        outlined: { borderColor: "rgba(166,107,125,0.35)" },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: "1px solid #F0E7DE",
          boxShadow: "0 6px 18px rgba(42,25,0,0.04), 0 1px 1px rgba(42,25,0,0.05)",
          background: "linear-gradient(180deg, rgba(166,107,125,0.04), rgba(255,255,255,0) 28%)",
        },
      },
    },
    MuiDialog: {
      defaultProps: { fullWidth: true, maxWidth: "sm" },
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 16,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.20)}`,
          boxShadow: "0 24px 60px rgba(0,0,0,.18)",
          backgroundImage: `
            radial-gradient(1200px 600px at -10% -10%, ${alpha(theme.palette.primary.main, .06)}, transparent 60%),
            radial-gradient(1000px 500px at 120% 0%, ${alpha(theme.palette.secondary.main, .06)}, transparent 55%),
            url("/wedding-texture-white.png")
          `,
          backgroundRepeat: "no-repeat, no-repeat, repeat",
          backgroundSize: "auto, auto, 1024px 1024px",
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: { backgroundColor: "rgba(0,0,0,.28)", backdropFilter: "blur(2px)" },
      },
    },
    MuiDialogTitle: { styleOverrides: { root: { fontWeight: 700, display: "flex", alignItems: "center", gap: 8 } } },
    MuiDialogContent: { styleOverrides: { root: { paddingTop: 12 } } },
    MuiDialogActions: { styleOverrides: { root: { padding: 16 } } },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 8, height: 10, backgroundColor: "#F1E8E0" },
        bar: { borderRadius: 8, backgroundColor: "#A66B7D" },
      },
    },
  },
});

export default function App() {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [guestName, setGuestName] = React.useState("");
  const [queue, setQueue] = React.useState<Queued[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [uploadPhase, setUploadPhase] =
    React.useState<"idle" | "uploading" | "success" | "error">("idle");

  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewSrc, setPreviewSrc] = React.useState<string | undefined>();
  const [previewName, setPreviewName] = React.useState<string | undefined>();
  const [previewKind, setPreviewKind] = React.useState<PreviewKind | undefined>();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileView, setMobileView] = React.useState<"grid" | "list">("list");

  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  const sig = (f: File) => `${f.name}::${f.size}::${f.lastModified}`;

  const onChoose = (files: FileList | null) => {
    if (!files?.length) return;
    const selectedFiles = Array.from(files);

    setQueue((prev) => {
      const bySig = new Map(prev.map((q) => [sig(q.file), q]));
      const seenNow = new Set<string>();
      const added: Queued[] = [];
      const bumpedOrder: string[] = [];

      for (const f of selectedFiles) {
        const s = sig(f);
        if (seenNow.has(s)) continue;
        seenNow.add(s);

        if (bySig.has(s)) {
          if (!bumpedOrder.includes(s)) bumpedOrder.push(s);
        } else {
          added.push({
            id: crypto.randomUUID(),
            file: f,
            preview: /^image\/|^video\//.test(f.type)
              ? URL.createObjectURL(f)
              : undefined,
          });
        }
      }

      const bumpedSet = new Set(bumpedOrder);
      const rest = prev.filter((q) => !bumpedSet.has(sig(q.file)));
      const bumpedItems = bumpedOrder.map((s) => bySig.get(s)!);
      return [...rest, ...added, ...bumpedItems];
    });

    if (inputRef.current) inputRef.current.value = "";
  };

  const removeOne = (id: string) => {
    setQueue((q) => {
      const item = q.find((x) => x.id === id);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return q.filter((x) => x.id !== id);
    });
    setSelected((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const clearAll = () => {
    queue.forEach((q) => q.preview && URL.revokeObjectURL(q.preview));
    setQueue([]);
    setSelected(new Set());
  };

  React.useEffect(() => {
    return () => {
      queue.forEach((q) => q.preview && URL.revokeObjectURL(q.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function simulateUpload() {
    return new Promise<{ result: number }>((resolve) => {
      let progress = 0;

      const interval = setInterval(() => {
        progress += Math.floor(Math.random() * 8) + 3;

        if (progress >= 100) {
          progress = 100;
          setProgress(progress);
          clearInterval(interval);

          setTimeout(() => {
            resolve({ result: 0 });
          }, 300);
        } else {
          setProgress(progress);
        }
      }, 150);
    });
  }

  const onUpload = async () => {
    if (!queue.length) return;

    setDialogOpen(true);
    setUploadPhase("uploading");
    setBusy(true);
    setProgress(0);

    // const files = queue
    //   .slice()
    //   .sort(
    //     (a, b) =>
    //       a.file.lastModified - b.file.lastModified ||
    //       a.file.name.localeCompare(b.file.name)
    //   )
    //   .map((q) => q.file);

    try {
      const data = await simulateUpload();

      if (data?.result !== 0) {
        setUploadPhase("error");
        return;
      }
    
      setUploadPhase("success");
    } finally {
      setBusy(false);
      setProgress(100);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    if (uploadPhase === "success") {
      clearAll();
    }
    setUploadPhase("idle");
    setProgress(0);
  };

  const handleDialogRequestClose = (_e: object, _reason: "backdropClick" | "escapeKeyDown") => {
    if (uploadPhase === "uploading") return;
    handleDialogClose();
  };

  const handlePreview = (src: string, name: string, kind: PreviewKind) => {
    setPreviewSrc(src);
    setPreviewName(name);
    setPreviewKind(kind);
    setPreviewOpen(true);
  };

  const toggleSelectOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(queue.map((q) => q.id)));
  const clearSelection = () => setSelected(new Set());
  const removeSelected = () => {
    if (!selected.size) return;
    setQueue((q) => {
      q.forEach((item) => {
        if (selected.has(item.id) && item.preview) URL.revokeObjectURL(item.preview);
      });
      return q.filter((item) => !selected.has(item.id));
    });
    setSelected(new Set());
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="md" sx={{ py: 5, pb: isMobile ? "calc(96px + env(safe-area-inset-bottom))" : 5 }}>
        <Stack spacing={2.5}>
          <Box
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
              color: "text.primary",
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0.75)),
                url("/image_623F0D5E-7DED-4B9A-A0D5-4007AC85CC86_1762349705.jpeg")
              `,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
            }}
          >
            <Typography variant="h1">Joanna & Mateusz</Typography>
            <Typography variant="h2">Wrzutka weselna</Typography>
            <Typography variant="body1" color="text.secondary" sx={{
                mt: 0.5,
                fontSize: "1.1rem",
                textAlign: "center",
                color: "#7D4A58",
                fontFamily: '"Italianno","Times New Roman",Times,serif',
              }}
            >
              Pokaż gospodarzom, jak wygląda to wydarzenie z Twojej perspektywy.
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
            <TextField
              fullWidth
              label="Pozwól Młodym Cię namierzyć..."
              placeholder="np. Tajemniczy Gość"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              sx={{
                "& > div > fieldset": { borderColor: "primary.light" },
                ...(guestName ? {"& > label": { color: "primary.main"}} : {})
              }}
            />
            {!isMobile && (
              <>
                <Tooltip title="Dodaj">
                  <span>
                    <Button onClick={() => inputRef.current?.click()} variant="contained" startIcon={<AddPhotoAlternateIcon />} disabled={busy}>
                      Dodaj pliki
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Wyślij Młodym">
                  <span>
                    <Button onClick={onUpload} variant="contained" color="primary" endIcon={<SendIcon />} disabled={!queue.length || busy}>
                      Wyślij Młodym
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Stack>

          {queue.length > 0 && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
              <Chip
                variant="outlined"
                color={queue.length ? (selected.size ? "primary" : "default") : "default"}
                label={`${selected.size} zaznaczonych`}
              />
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={selectAll} disabled={!queue.length || selected.size === queue.length}>
                  Zaznacz wszystko
                </Button>
                <Button size="small" onClick={clearSelection} disabled={!selected.size}>
                  Odznacz wszystko
                </Button>
                <Button size="small" variant="contained" onClick={removeSelected} disabled={!selected.size}>
                  Usuń zaznaczone
                </Button>
              </Stack>

              {isMobile && (
                <Box sx={{ ml: { sm: "auto" } }}>
                  <ToggleButtonGroup
                    size="small"
                    fullWidth
                    exclusive
                    value={mobileView}
                    onChange={(_, v) => v && setMobileView(v)}
                    color="primary"
                    sx={{ borderColor: "primary.light" }}
                  >
                    <ToggleButton sx={{ borderColor: "primary.light" }} fullWidth value="grid">Kafelki</ToggleButton>
                    <ToggleButton sx={{ borderColor: "primary.light" }} fullWidth value="list">Lista</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
            </Stack>
          )}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            hidden
            onChange={(e) => onChoose(e.target.files)}
          />

          {queue.length > 0 && (
            isMobile && mobileView === "list" ? (
              <Box>
                {queue.map((q) => {
                  const isImage = q.file.type.startsWith("image/");
                  const isVideo = q.file.type.startsWith("video/");
                  return (
                    <Box
                      key={q.id}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleSelectOne(q.id)
                      }}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "64px 1fr auto",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        border: "1px solid",
                        borderColor: "primary.light",
                        borderRadius: 1.5,
                        mb: 1,
                        bgcolor: "transparent",
                        outline: selected.has(q.id) ? "2px solid" : "none",
                        outlineColor: selected.has(q.id) ? "primary.main" : "transparent",
                      }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 1,
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "background.default",
                          cursor: (isImage || isVideo) && q.preview ? "pointer" : "default",
                          pointerEvents: "none",
                        }}
                      >
                        {isImage && q.preview ? (
                          <img src={q.preview} alt={q.file.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : isVideo && q.preview ? (
                          <video src={q.preview} muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <InsertDriveFileIcon />
                        )}
                      </Box>

                      <Box sx={{ overflow: "hidden" }}>
                        <Typography variant="body2" noWrap title={q.file.name}>
                          {q.file.name}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="Usuń">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                removeOne(q.id);
                              }}
                              disabled={busy}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(auto-fill, minmax(160px, 1fr))",
                    sm: `repeat(auto-fill, minmax(${CARD_W}px, 1fr))`,
                  },
                  gap: { xs: 1.5, sm: 2 },
                  justifyItems: "stretch",
                  alignItems: "stretch",
                }}
              >
                {queue.map((q) => (
                  <Box key={q.id} sx={{ width: "100%" }}>
                    <FileCard
                      id={q.id}
                      file={q.file}
                      preview={q.preview}
                      busy={busy}
                      onRemove={removeOne}
                      onPreview={handlePreview}
                      selected={selected.has(q.id)}
                      onToggleSelect={toggleSelectOne}
                      isMobile={isMobile}
                    />
                  </Box>
                ))}
              </Box>
            )
          )}
        </Stack>
      </Container>

      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            left: 16,
            right: 16,
            bottom: "calc(8px + env(safe-area-inset-bottom))",
            zIndex: (t) => t.zIndex.appBar
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              background:
                "radial-gradient(1000px at -10% -20%, rgba(200,122,122,0.10), transparent 60%), radial-gradient(800px at 120% 0%, rgba(127,163,155,0.10), transparent 55%)",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
              display: "flex",
              gap: 2,
              backdropFilter: "blur(5px)",
            }}
          >
            <Button
              onClick={() => inputRef.current?.click()}
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<AddPhotoAlternateIcon />}
              disabled={busy}
              sx={{ backgroundColor: "#FFFBF7" }}
            >
              Dodaj
            </Button>
            <Button
              onClick={onUpload}
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              endIcon={<SendIcon />}
              disabled={!queue.length || busy}
            >
              Wyślij
            </Button>
          </Box>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={handleDialogRequestClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {uploadPhase === "success" && <CheckCircleOutlineIcon color="success" />}
          {uploadPhase === "uploading" ? "Wysyłanie plików" : uploadPhase === "success" ? "Wysłano" : "Wysłano"}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{
            ...(["success", "uploading"].includes(uploadPhase) ? {
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            } : {})
          }}>
            {uploadPhase === "uploading" && (
              <>
                <CircularProgress variant="determinate" size={100} thickness={3} value={progress} sx={{ height: 100, borderRadius: "50%" }} />
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ marginTop: "0 !important", position: "absolute", fontWeight: 600, color: 'primary.dark', fontFamily: '"Italianno","Times New Roman",Times,serif', fontSize: "1.5rem" }}
                >{`${Math.round(progress)}%`}</Typography>
              </>
            )}
            {uploadPhase === "success" && <Alert severity="success" icon={<></>} sx={{ backgroundColor: "transparent", fontFamily: '"Italianno","Times New Roman",Times,serif', fontSize: "2rem", display: "flex", justifyContent: "center", "& > div:first-of-type": { display: "none" } }}>Dziękujemy za wrzutkę!</Alert>}
          </Stack>
        </DialogContent>

        <DialogActions>
          {uploadPhase === "uploading" ? <Button disabled>Wysyłanie…</Button> : <Button onClick={handleDialogClose} variant="contained">Zamknij</Button>}
        </DialogActions>
      </Dialog>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="md" fullScreen={isMobile}>
        <DialogTitle sx={{ pr: 6 }}>
          <Typography
            variant="subtitle1"
            noWrap
            sx={{ fontWeight: 700, color: "text.primary" }}
            title={previewName || "Podgląd"}
          >
            {previewName || "Podgląd"}
          </Typography>

          <IconButton
            aria-label="Zamknij podgląd"
            onClick={() => setPreviewOpen(false)}
            size="medium"
            sx={(t) => ({
              position: "absolute",
              right: 12,
              top: 12,
              width: 36,
              height: 36,
              color: "#A66B7D",
              "&:hover": { backgroundColor: alpha(t.palette.primary.main, 0.16) },
            })}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: "flex", justifyContent: "center" }}>
          {previewSrc && previewKind === "video" && (
            <video key={previewSrc} src={previewSrc} controls autoPlay playsInline style={{ display: "block", width: "100%", height: "auto", background: "#000" }} />
          )}
          {previewSrc && previewKind === "image" && (
            <img key={previewSrc} src={previewSrc} alt={previewName} style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain", display: "block", background: "#000" }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
